import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timesheets, type TimesheetItem, type AutofillDay } from '@/api/timesheets';
import {
  dayCellText, isWorkDay, parseDayCell, summarizeDays, zeroWorkDays,
  type TimesheetDayCell,
} from '@/shared/utils/timesheetCells';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  norm_days: number;
  comment: string;
  items: TimesheetItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  period_year: new Date().getFullYear(),
  period_month: new Date().getMonth() + 1,
  norm_days: 0,  // порожнє при створенні — бухгалтер вводить або «Заповнити за графіком»
  comment: '',
  items: [],
};

const newRow = (n: number, employeeId: string, norm: number): TimesheetItem => ({
  line_number: n,
  employee_id: employeeId,
  working_days: String(norm),
  vacation_days: '0',
  sick_days: '0',
  absence_days: '0',
});

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const fmt = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function recalcWorking(
  item: TimesheetItem, norm: number,
  recalcHours: boolean, hoursPerDay: number | undefined,
): TimesheetItem {
  const v = Number(item.vacation_days || 0);
  const s = Number(item.sick_days || 0);
  const a = Number(item.absence_days || 0);
  const wd = Math.max(0, norm - v - s - a);
  const next: TimesheetItem = { ...item, working_days: wd.toFixed(2) };
  // Години слідують за відпрацьованими днями × годин-на-день З ГРАФІКА працівника
  // (для 4-денки 35год/тиждень це 8.75, а не 8). Окрім рядків де години введено
  // вручну (recalcHours=false) — їх не чіпаємо. Якщо графік невідомий — години
  // не чіпаємо взагалі (краще лишити як є, ніж проставити невірне ×8).
  if (recalcHours && hoursPerDay != null) {
    next.worked_hours = (wd * hoursPerDay).toFixed(2);
  }
  return next;
}

export default function TimesheetEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['timesheet', id],
    queryFn: () => timesheets.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.timesheets', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  const v = useFormValidation();
  // Спосіб введення (як 1С): summary = зведено, daily = сітка по днях 1..31.
  const [viewMode, setViewMode] = useState<'summary' | 'daily'>('summary');
  // Посуточна сітка (frontend-state; у БД зберігаються лише підсумки working/hours).
  // Ключ — employee_id; значення — масив днів за графіком/календарем.
  const [daysByEmp, setDaysByEmp] = useState<Record<string, DayCell[]>>({});
  // Заповнення «Усіма»: лише штатні чи усі (включно з ЦПУ). За замовч. — штатні.
  const [staffOnly, setStaffOnly] = useState(true);
  // Рядки (за індексом), де години введено ВРУЧНУ — авто-перерахунок їх НЕ чіпає.
  // Чисто UI-стан, у БД не йде. recalcWorking перераховує години лише для
  // «авто»-рядків (не у цьому наборі).
  const [manualHours, setManualHours] = useState<Set<number>>(new Set());
  // Годин/день за графіком кожного працівника (employee_id → hours_per_day).
  // Наповнюється з autofill-відповіді. Використовується для перерахунку годин при
  // ручній правці днів відсутності: години = відпрацьовані дні × hours_per_day.
  const [hoursPerDayByEmp, setHoursPerDayByEmp] = useState<Record<string, number>>({});

  function validate(): boolean {
    return v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату документа'],
      ['norm_days', form.norm_days, 'Вкажіть норму днів (1–31)'],
    ]);
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!validate()) return;
    if (form.items.length === 0) {
      setError('Додайте співробітників перед проведенням');
      return;
    }
    createAndPost.mutate();
  }
  // Для збереженого документа: «Провести» спершу зберігає правки, потім проводить.
  function onUpdateAndPost() {
    if (!validate()) return;
    if (form.items.length === 0) {
      setError('Додайте співробітників перед проведенням');
      return;
    }
    updateAndPost.mutate();
  }

  const empQ = useQuery({
    // Табель — не звільнені (active_only). staffOnly: лише STAFF чи усі (ЦПУ теж).
    queryKey: ['employees', 'timesheet', form.organization_id, staffOnly],
    queryFn: () => refs.employees(form.organization_id, {
      ...(staffOnly ? { employment_type: 'STAFF' } : {}),
      active_only: true,
    }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        norm_days: d.norm_days,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          employee_id: it.employee_id,
          working_days: String(it.working_days),
          vacation_days: String(it.vacation_days),
          sick_days: String(it.sick_days),
          absence_days: String(it.absence_days),
          worked_hours: it.worked_hours ?? null,
          days: it.days ?? null,
          comment: it.comment,
        })),
      });
      // Відновити посуточну сітку (режим «По днях») з days кожного рядка (0300).
      const grid: Record<string, DayCell[]> = {};
      for (const it of d.items) {
        if (it.days && it.days.length) {
          grid[it.employee_id] = it.days.map((dd) => ({
            day: dd.day, is_work: dd.code === 'W',
            hours: dd.hours, code: dd.code as DayCell['code'],
          }));
        }
      }
      setDaysByEmp(grid);
    }
  }, [isNew, docQuery.data, availableOrgs]);

  // Підвантажити годин/день за графіком для працівників ТЧ, яких ще нема у кеші —
  // щоб ручна правка днів відсутності одразу правильно перераховувала години
  // (за графіком людини, не ×8). Легкий запит; нічого не зберігає.
  useEffect(() => {
    const missing = Array.from(new Set(
      form.items
        .map((it) => it.employee_id)
        .filter((id) => id && !(id in hoursPerDayByEmp)),
    ));
    if (missing.length === 0 || !form.organization_id) return;
    let cancelled = false;
    timesheets.hoursPerDay({ organization_id: form.organization_id, employee_ids: missing })
      .then((res) => {
        if (cancelled) return;
        const add: Record<string, number> = {};
        for (const [emp, hpd] of Object.entries(res)) {
          if (hpd != null) add[emp] = hpd;
        }
        if (Object.keys(add).length) setHoursPerDayByEmp((prev) => ({ ...prev, ...add }));
      })
      .catch(() => { /* графік невідомий — години не чіпаємо, не помилка */ });
    return () => { cancelled = true; };
  }, [form.items, form.organization_id, hoursPerDayByEmp]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWorking = form.items.reduce((s, e) => s + Number(e.working_days || 0), 0);
  const totalVacation = form.items.reduce((s, e) => s + Number(e.vacation_days || 0), 0);
  const totalSick = form.items.reduce((s, e) => s + Number(e.sick_days || 0), 0);
  const totalAbsence = form.items.reduce((s, e) => s + Number(e.absence_days || 0), 0);

  // Зберегти посуточну сітку (режим «По днях») у days кожного рядка → у БД (0300).
  // Якщо сітки для працівника нема (режим «Зведено») — days=null (як було).
  function itemsWithDays(): TimesheetItem[] {
    return form.items.map((it) => {
      const grid = daysByEmp[it.employee_id];
      return {
        ...it,
        days: grid && grid.length
          ? grid.map((d) => ({ day: d.day, code: d.code ?? (d.is_work ? 'W' : 'OFF'), hours: d.hours }))
          : null,
      };
    });
  }

  const create = useMutation({
    mutationFn: () => timesheets.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      norm_days: form.norm_days,
      comment: form.comment || null,
      items: itemsWithDays(),
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate(`/timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Зберегти та провести (як у наказах): create → post → перехід на документ.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await timesheets.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        norm_days: form.norm_days,
        comment: form.comment || null,
        items: itemsWithDays(),
      });
      try {
        await timesheets.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/timesheets/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate(`/timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => timesheets.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      norm_days: form.norm_days,
      comment: form.comment || null,
      items: itemsWithDays(),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timesheet', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Зберегти та провести (для вже збереженого документа): спершу зберігаємо
  // правки форми у БД, потім проводимо — щоб «Провести» не ігнорувало
  // незбережені зміни (раніше треба було тиснути «Зберегти», тоді «Провести»).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await timesheets.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        norm_days: form.norm_days,
        comment: form.comment || null,
        items: itemsWithDays(),
      });
      await timesheets.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['timesheets'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => timesheets.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['timesheets'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => timesheets.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate('/timesheets');
    },
  });

  // Авто-заповнення за графіком+календарем (Порція C/D). Бере всіх штатних
  // (або вже доданих) → autofill endpoint → заповнює рядки + сітку + норму.
  const autofill = useMutation({
    mutationFn: () => {
      const empIds = form.items.length > 0
        ? form.items.filter((it) => it.employee_id).map((it) => it.employee_id)
        : (empQ.data ?? []).map((e) => e.id);
      if (empIds.length === 0) throw new Error('Немає співробітників для заповнення');
      return timesheets.autofill({
        organization_id: form.organization_id,
        period_year: form.period_year,
        period_month: form.period_month,
        employee_ids: empIds,
      });
    },
    onSuccess: (res) => {
      const grid: Record<string, DayCell[]> = {};
      const items: TimesheetItem[] = res.items.map((r, idx) => {
        grid[r.employee_id] = r.days.map((d: AutofillDay) => ({
          ...d, code: (d.is_work ? 'W' : 'OFF') as DayCell['code'],
        }));
        return {
          line_number: idx + 1,
          employee_id: r.employee_id,
          working_days: String(r.working_days),
          vacation_days: '0',
          sick_days: '0',
          absence_days: '0',
          // ч.1209: жодного робочого дня (тривала відсутність) — години явно 0, не «не заповнено».
          worked_hours: r.working_days === 0 ? '0' : (r.worked_hours ? String(r.worked_hours) : null),
          // Причина неробочих днів за наказом про відсутність — у коментарі рядка.
          comment: r.absence_note ?? null,
        };
      });
      // Кешуємо годин/день за графіком кожного працівника — для перерахунку
      // годин при подальшій ручній правці днів відсутності у «Зведено».
      const hpd: Record<string, number> = {};
      for (const r of res.items) {
        if (r.hours_per_day != null) hpd[r.employee_id] = r.hours_per_day;
      }
      setHoursPerDayByEmp((prev) => ({ ...prev, ...hpd }));
      // Посуточну сітку зберігаємо ЛИШЕ якщо користувач реально працює у режимі
      // «По днях». У «Зведено» autofill заповнює тільки підсумки (норма/відпрацьовано),
      // а days лишаємо порожніми — інакше сітка мовчки збережеться у БД і потім
      // здаватиметься, що бухгалтер «заповнив по днях», хоча не заповнював.
      setDaysByEmp(viewMode === 'daily' ? grid : {});
      // ч.1280: норма від сервера 0 (нема з чого рахувати) не стирає введену бухгалтером.
      setForm((f) => ({ ...f, norm_days: res.norm_days || f.norm_days, items }));
    },
    onError: (e) => setError(extractError(e)),
  });

  // Зібрати рядки з денних табелів за місяць (Порція E). Підсумовує дні/години.
  const buildDaily = useMutation({
    mutationFn: () => timesheets.buildFromDaily({
      organization_id: form.organization_id,
      period_year: form.period_year,
      period_month: form.period_month,
    }),
    onSuccess: (res) => {
      if (res.items.length === 0) {
        setError(`Денних табелів за період не знайдено (${res.daily_count}).`);
        return;
      }
      const grid: Record<string, DayCell[]> = {};
      const items: TimesheetItem[] = res.items.map((r, idx) => {
        // Заповнюємо й сітку (режим «по днях») — інакше комірки порожні.
        grid[r.employee_id] = r.days.map((d) => ({
          day: d.day, is_work: d.is_work, hours: d.hours,
          code: d.code as DayCell['code'],
        }));
        return {
          line_number: idx + 1,
          employee_id: r.employee_id,
          working_days: String(r.working_days),
          vacation_days: String(r.vacation_days),
          sick_days: String(r.sick_days),
          absence_days: String(r.absence_days),
          worked_hours: r.worked_hours ? String(r.worked_hours) : null,
          comment: null,
        };
      });
      setDaysByEmp(grid);
      setForm((f) => ({ ...f, items }));
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });

  function setItem(i: number, patch: Partial<TimesheetItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // Якщо змінюються причини відсутності — перераховуємо working_days від норми
        // (+ години = дні × годин/день з графіка, якщо години не введено вручну).
        const trigger = ['vacation_days', 'sick_days', 'absence_days'];
        if (trigger.some((k) => k in patch)) {
          return recalcWorking(next, f.norm_days, !manualHours.has(idx), hoursPerDayByEmp[next.employee_id]);
        }
        return next;
      }),
    }));
  }

  function recalcAllWorking() {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) =>
        recalcWorking(e, f.norm_days, !manualHours.has(idx), hoursPerDayByEmp[e.employee_id])),
    }));
  }

  function fillFromEmployees() {
    if (!empQ.data || empQ.data.length === 0) return;
    setForm((f) => ({
      ...f,
      items: empQ.data!.map((e, idx) => newRow(idx + 1, e.id, f.norm_days)),
    }));
  }

  // Режим «по днях»: введення у комірку. Парсимо текст у код:
  //  «В»/«в» → відпустка, «Х»/«х» → лікарняний, «П»/«п» → прогул,
  //  число → робочий день з цими годинами, порожньо → вихідний/неробочий.
  function setDayValue(empId: string, dayIdx: number, raw: string) {
    if (isPosted) return;
    const parsed = parseDayCell(raw);
    setDaysByEmp((prev) => {
      const days = prev[empId];
      if (!days) return prev;
      const updated = days.map((d, i) =>
        i === dayIdx ? { ...d, ...parsed } : d,
      );
      recalcRowFromGrid(empId, updated);
      return { ...prev, [empId]: updated };
    });
  }

  // Перерахунок підсумків рядка з сітки днів (робочі/В/Х/П).
  // ч.1207: робочий день з 0 годин — не відпрацьований (у дні не входить), але
  // години рядка завжди число: сітка є → 0, а не «—» (null = «не заповнено»).
  function recalcRowFromGrid(empId: string, days: DayCell[]) {
    const sum = summarizeDays(days);
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => it.employee_id === empId
        ? {
            ...it, working_days: String(sum.working_days),
            worked_hours: String(sum.worked_hours),
            vacation_days: String(sum.vacation_days), sick_days: String(sum.sick_days),
            absence_days: String(sum.absence_days),
          }
        : it),
    }));
  }

  // ч.1207 (рішення власника): нульовий табель одним натисканням — усім 0
  // відпрацьованих днів і 0 годин; у сітці робочі дні стають «0» (не прочерк),
  // відпустки/лікарняні/прогули лишаються.
  function zeroAll() {
    if (isPosted) return;
    setDaysByEmp((prev) => {
      const next: Record<string, DayCell[]> = {};
      for (const [emp, days] of Object.entries(prev)) next[emp] = zeroWorkDays(days);
      return next;
    });
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => ({ ...it, working_days: '0', worked_hours: '0' })),
    }));
  }

  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
    // Зсунути індекси manualHours: прибрати i, індекси > i зменшити на 1.
    setManualHours((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      }
      return next;
    });
  }

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати табель у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/timesheets')}>Назад</button>
        </div>
      </div>
    );
  }

  // ч.878 (новий дизайн, Закон #10): «ще дії» (ч.814: створити нарахування) →
  // «Повʼязані дії» правої колонки — дослівно.
  const railActions = [{
    label: 'створити нарахування',
    // ч.1347: нарахування — зі збереженого табеля, проведення не чекаємо.
    title: isNew ? 'Спершу збережіть табель'
      : 'Основна відомість за цей місяць, заповнена з цього табеля',
    disabled: isNew,
    onClick: () => navigate(`/payrolls/new?kind=MAIN&org=${form.organization_id}`
      + `&year=${form.period_year}&month=${form.period_month}&timesheet=${id}`),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'period', title: 'Період і норма', required: 3,
      filled: (form.period_month ? 1 : 0) + (form.period_year ? 1 : 0) + (form.norm_days ? 1 : 0) },
    { id: 'items', title: 'Співробітники', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий табель' : `Табель № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/timesheets')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending || createAndPost.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити табель?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/timesheets/${id}/print`)}
          entriesDoc={{ type: 'doc.timesheets', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {v.banner()}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.timesheets', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => { setForm({ ...form, organization_id: e.target.value }); v.clear('organization_id'); }}
                  disabled={!isNew}
                  style={v.inputStyle('organization_id')}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
          {v.error('organization_id')}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => { setForm({ ...form, document_number: e.target.value }); v.clear('document_number'); }}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted}
                 style={v.inputStyle('document_number')} />
          {v.error('document_number')}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(val) => { setForm({ ...form, document_date: val }); v.clear('document_date'); }}
                 disabled={isPosted}
                 style={v.inputStyle('document_date')} />
          {v.error('document_date')}
        </div>
      </div>
      </DocBlock>

      {/* ч.716 (рішення власника): період нарахування — окремим рядком (блок 2). */}
      <DocBlock index={2} title="Період і норма">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Місяць нарахування *</label>
          <select value={form.period_month}
                  onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
                  disabled={isPosted}>
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Рік нарахування *</label>
          <input type="number" value={form.period_year}
                 onChange={(e) => setForm({ ...form, period_year: Number(e.target.value) })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Норма днів *</label>
          <input type="number" min={1} max={31} value={form.norm_days || ''}
                 onChange={(e) => { setForm({ ...form, norm_days: Number(e.target.value) }); v.clear('norm_days'); }}
                 onBlur={recalcAllWorking}
                 disabled={isPosted}
                 style={v.inputStyle('norm_days')} />
          {v.error('norm_days')}
        </div>
      </div>

      </DocBlock>

      {/* ч.715 (рішення власника): дії заповнення; недоступні за станом — disabled
          (Закон #6/ч.702). ч.878 — у заголовку блоку «Співробітники». */}
      <DocBlock index={3} title="Співробітники"
        actions={!isPosted ? (<>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}
                 title="ЦПУ — договори цивільно-правового характеру (не штатні)">
            <input type="checkbox" checked={!staffOnly}
                   onChange={(e) => setStaffOnly(!e.target.checked)} />
            включно з ЦПУ
          </label>
          <button type="button" className="btn-doc" onClick={fillFromEmployees}
            disabled={!empQ.data || empQ.data.length === 0 || form.items.length > 0}
            title={!empQ.data || empQ.data.length === 0
              ? 'Немає співробітників за вибіркою'
              : form.items.length > 0
                ? 'Таблиця вже має рядки — «усіма» лише у порожню (щоб не перетерти введене)'
                : 'Додати рядки для всіх співробітників (без розрахунку днів)'}>
            усіма{empQ.data && empQ.data.length > 0 ? ` (${empQ.data.length}${staffOnly ? ', штатні' : ', усі'})` : ''}
          </button>
          <button type="button" className="btn-doc" onClick={() => buildDaily.mutate()} disabled={buildDaily.isPending}
            title="Зібрати підсумки з денних табелів за цей місяць та організацію">
            зібрати з денних
          </button>
          <button className="primary" onClick={() => autofill.mutate()} disabled={autofill.isPending}
            title="Розрахувати робочі дні/години за графіком співробітника та виробничим календарем">
            заповнити за графіком
          </button>
          <button type="button" className="btn-doc" onClick={zeroAll}
            disabled={form.items.length === 0}
            title={form.items.length === 0
              ? 'Спершу додайте співробітників'
              : 'Нульовий табель: усім 0 відпрацьованих днів і 0 годин; у сітці робочі дні стають «0», відпустки/лікарняні/прогули не чіпаються'}>
            обнулити
          </button>
        </>) : undefined}>
      {/* ч.716: перемикач способу введення (як 1С); ч.878 — канонічні seg-tabs. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          {(['summary', 'daily'] as const).map((m) => (
            <button key={m} type="button" className={viewMode === m ? 'active' : ''}
              onClick={() => setViewMode(m)}>
              {m === 'summary' ? 'Зведено за період' : 'По днях періоду'}
            </button>
          ))}
        </div>
      </div>
      {viewMode === 'daily' && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
          Сітка по днях: «8» = робочий день (8 год), «0» = робочий день без відпрацьованих годин, порожньо = вихідний/свято. Клік відкриває дії для дня. Підсумки рахуються автоматично. «Заповнити за графіком» проставить дні з графіка + календаря; «обнулити» зробить нульовий табель.
        </p>
      )}

      {viewMode === 'daily' && (
        <DailyGrid
          items={form.items}
          daysByEmp={daysByEmp}
          empOptions={empQ.data ?? []}
          isPosted={isPosted}
          daysInMonth={new Date(form.period_year, form.period_month, 0).getDate()}
          year={form.period_year}
          month={form.period_month}
          onSetDay={setDayValue}
          onRemove={removeRow}
        />
      )}

      {viewMode === 'summary' && (
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 25 }}>#</th>
            <th>Співробітник</th>
            <th style={{ width: 90 }}>Відпустка</th>
            <th style={{ width: 90 }}>Лікарняний</th>
            <th style={{ width: 90 }}>Прогули</th>
            <th style={{ width: 100 }}>Відпрацьовано</th>
            <th style={{ width: 85 }}>Години</th>
            <th>Коментар</th>
            <th style={{ width: 35 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <select value={it.employee_id}
                        onChange={(e) => setItem(i, { employee_id: e.target.value })}
                        disabled={isPosted}
                        style={{ width: '100%' }}>
                  <option value="">—</option>
                  {empQ.data?.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}{e.code ? ` — ${e.code}` : ''}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.vacation_days}
                       onChange={(e) => setItem(i, { vacation_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.sick_days}
                       onChange={(e) => setItem(i, { sick_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.absence_days}
                       onChange={(e) => setItem(i, { absence_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.working_days}
                       onChange={(e) => {
                         // ч.1203 (бій, Медоїд): дні обнулили руками, а години лишились
                         // 176 → відомість показувала «0 дн | 176 год». Години слідують
                         // за днями (дні × годин/день з графіка), окрім рядків, де години
                         // введено вручну.
                         const v = e.target.value;
                         const hpd = hoursPerDayByEmp[it.employee_id];
                         // ч.1207: 0 днів → 0 годин і без відомого графіка (нульовий табель).
                         const auto = !manualHours.has(i) && v !== ''
                           && (hpd != null || Number(v) === 0);
                         setItem(i, {
                           working_days: v,
                           ...(auto ? { worked_hours: (Number(v || 0) * (hpd ?? 0)).toFixed(2) } : {}),
                         });
                       }}
                       disabled={isPosted}
                       title="Відпрацьовані дні; години перераховуються за графіком (якщо не введені вручну)"
                       style={{ textAlign: 'right', width: '100%', fontWeight: 600, background: 'var(--bg-soft)' }} />
              </td>
              <td>
                <input type="number" step="0.5" min={0} className="no-spin"
                       value={it.worked_hours ?? ''}
                       onChange={(e) => {
                         // Ручний ввід годин — позначаємо рядок, щоб авто-перерахунок
                         // (дні×годин-день) більше його не затирав.
                         setManualHours((prev) => new Set(prev).add(i));
                         setItem(i, { worked_hours: e.target.value || null });
                       }}
                       disabled={isPosted} placeholder="—"
                       title="Години (з графіка авто; можна ввести вручну для неповного дня)"
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td>
                <input value={it.comment ?? ''}
                       onChange={(e) => setItem(i, { comment: e.target.value })}
                       disabled={isPosted}
                       style={{ width: '100%' }} />
              </td>
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.2rem 0.45rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontSize: '0.78rem' }}>
            <td colSpan={2} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalVacation)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalSick)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalAbsence)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWorking)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </TchTable>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.timesheets'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Табель — інформаційний документ. Після проведення його можна обрати у нарахуванні ЗП:
          оклад буде поділено пропорційно (оклад × відпрацьовано / норма).
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

const WD_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];

// Комірка дня сітки: код W=робота, V=відпустка, S=лікарняний, A=прогул, OFF=вихідний.
// Парсинг/підпис/підсумки — у shared/utils/timesheetCells.ts (ч.1207: «0» на робочому
// дні — робочий день без годин, не вихідний).
type DayCell = TimesheetDayCell;

interface DailyGridProps {
  items: TimesheetItem[];
  daysByEmp: Record<string, DayCell[]>;
  empOptions: { id: string; code?: string | null; name: string }[];
  isPosted: boolean;
  daysInMonth: number;
  year: number;
  month: number;
  onSetDay: (empId: string, dayIdx: number, raw: string) => void;
  onRemove: (lineIndex: number) => void;
}

// Колір комірки за кодом дня.
function cellStyle(d?: DayCell): { bg: string; color: string } {
  if (!d) return { bg: '#f1f5f9', color: '#cbd5e1' };
  if (d.code === 'V') return { bg: '#eff6ff', color: '#2563eb' };   // відпустка
  if (d.code === 'S') return { bg: '#fef2f2', color: '#dc2626' };   // лікарняний
  if (d.code === 'A') return { bg: '#fffbeb', color: '#d97706' };   // прогул
  if (isWorkDay(d)) {
    // ч.1207: робочий день без годин — «0» приглушеним, не прочерк.
    if (!(Number(d.hours) > 0)) return { bg: '#ecfdf5', color: 'var(--text-secondary)' };
    return { bg: '#ecfdf5', color: 'var(--badge-green-fg)' };
  }
  return { bg: '#f1f5f9', color: '#cbd5e1' };
}

/** Сітка по днях (як 1С «По дням периода»): рядок=співробітник, колонки 1..31.
 *  Комірка редагується: число=години роботи, В/Х/П=відпустка/лікарняний/прогул,
 *  порожньо=вихідний. Підсумки рахуються автоматично. */
function DailyGrid({
  items, daysByEmp, empOptions, isPosted, daysInMonth, year, month,
  onSetDay, onRemove,
}: DailyGridProps) {
  // Активна комірка (клік) → показуємо панель дій під сіткою.
  const [active, setActive] = useState<{ empId: string; idx: number } | null>(null);
  const [hoursInput, setHoursInput] = useState('');

  const empName = (id: string) => {
    const e = empOptions.find((o) => o.id === id);
    return e ? `${e.name}${e.code ? ' — ' + e.code : ''}` : '—';
  };
  const dayList = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const wd = (d: number) => (new Date(year, month - 1, d).getDay() + 6) % 7;

  if (items.length === 0) {
    return (
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        Немає рядків. Натисніть «Заповнити за графіком» або «Усіма».
      </p>
    );
  }

  function openCell(empId: string, idx: number) {
    if (isPosted) return;
    setActive({ empId, idx });
    setHoursInput('');
  }
  function apply(raw: string) {
    if (!active) return;
    onSetDay(active.empId, active.idx, raw);
  }

  return (
    <>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
        <table className="tch" style={{ whiteSpace: 'nowrap' }}>
          <thead>
            <tr>
              {!isPosted && <th style={{ width: 24, position: 'sticky', left: 0, background: 'var(--bg-soft-2)' }} />}
              <th style={{ position: 'sticky', left: isPosted ? 0 : 24, background: 'var(--bg-soft-2)', textAlign: 'left', minWidth: 170 }}>
                Співробітник
              </th>
              {dayList.map((d) => {
                const we = wd(d) >= 5;
                return (
                  <th key={d} style={{ width: 30, textAlign: 'center', color: we ? '#dc2626' : '#334155', fontWeight: we ? 700 : 500 }}>
                    <div>{d}</div>
                    <div style={{ fontSize: '0.62rem', fontWeight: 400 }}>{WD_SHORT[wd(d)]}</div>
                  </th>
                );
              })}
              <th style={{ textAlign: 'center', minWidth: 40 }}>Дн</th>
              <th style={{ textAlign: 'center', minWidth: 48 }}>Год</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, rowIdx) => {
              const days = daysByEmp[it.employee_id] ?? [];
              return (
                <tr key={it.employee_id || it.line_number}>
                  {!isPosted && (
                    <td style={{ textAlign: 'center', position: 'sticky', left: 0, background: 'var(--bg-panel)' }}>
                      <button onClick={() => onRemove(rowIdx)} className="danger"
                        title="Видалити рядок"
                        style={{ padding: '0.1rem 0.35rem', fontSize: '0.75rem' }}>×</button>
                    </td>
                  )}
                  <td style={{ position: 'sticky', left: isPosted ? 0 : 24, background: 'var(--bg-panel)' }}>
                    {empName(it.employee_id)}
                  </td>
                  {dayList.map((d, idx) => {
                    const day = days[idx];
                    const st = cellStyle(day);
                    const isActive = active?.empId === it.employee_id && active?.idx === idx;
                    return (
                      <td key={d}
                        onClick={() => openCell(it.employee_id, idx)}
                        title={isPosted ? undefined : 'Клік — обрати дію для дня'}
                        style={{
                          padding: '0.25rem 0', textAlign: 'center',
                          background: st.bg, color: st.color, fontWeight: 600,
                          cursor: isPosted ? 'default' : 'pointer', userSelect: 'none',
                          outline: isActive ? '2px solid var(--primary)' : 'none',
                          outlineOffset: -2,
                        }}>
                        {dayCellText(day) || '·'}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                    {it.working_days}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {it.worked_hours ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Панель дій для активної комірки (з'являється після кліку). */}
      {active && !isPosted && (() => {
        const it = items.find((x) => x.employee_id === active.empId);
        return (
          <div style={{
            marginTop: '0.5rem', padding: '0.6rem 0.8rem',
            border: '1px solid var(--primary)', borderRadius: 8,
            background: 'var(--bg-soft-2)', display: 'flex', gap: '0.5rem',
            alignItems: 'center', flexWrap: 'wrap',
          }}>
            <b style={{ fontSize: '0.85rem' }}>
              {it ? empName(it.employee_id) : ''} — {active.idx + 1}
              {' '}{MONTHS[month - 1]}:
            </b>
            <button onClick={() => apply('')} title="Очистити (вихідний)"
              style={{ padding: '0.3rem 0.6rem' }}>✕ Очистити</button>
            <button onClick={() => apply('0')} title="Робочий день без відпрацьованих годин"
              style={{ padding: '0.3rem 0.6rem' }}>0 — без годин</button>
            <button onClick={() => apply('П')} title="Прогул"
              style={{ padding: '0.3rem 0.6rem', color: '#d97706' }}>П — прогул</button>
            <button onClick={() => apply('Л')} title="Лікарняний"
              style={{ padding: '0.3rem 0.6rem', color: '#dc2626' }}>Л — лікарняний</button>
            <button onClick={() => apply('В')} title="Відпустка"
              style={{ padding: '0.3rem 0.6rem', color: '#2563eb' }}>В — відпустка</button>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input type="number" step="0.5" min={0} max={24}
                value={hoursInput} placeholder="години"
                onChange={(e) => setHoursInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && hoursInput) { apply(hoursInput); setHoursInput(''); }
                }}
                style={{ width: 80 }} />
              <button onClick={() => { if (hoursInput) { apply(hoursInput); setHoursInput(''); } }}
                className="primary" style={{ padding: '0.3rem 0.6rem' }}>↵</button>
            </span>
            <button onClick={() => setActive(null)}
              style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem' }}>Закрити</button>
          </div>
        );
      })()}

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
        Клік по дню → панель дій: <b>число</b> годин (Enter) · <b>0</b> робочий день
        без годин · <b>В</b> відпустка · <b> Л</b> лікарняний · <b>П</b> прогул ·
        <b>✕</b> очистити (вихідний).
      </p>
    </>
  );
}
